import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import { useEffect, useRef, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import IconButton from "@mui/material/IconButton";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import CircularProgress from "@mui/material/CircularProgress";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";

// Material Dashboard components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import AlertBox from "components/Alert/alert";

// Data & services
import categoryTableData from "layouts/categories/data/categoryTableData";
import {
  getCategories,
  createCategory,
  updateCategory,
} from "services/apiService";
import { API_ORIGIN } from "services/api";

// Slugify a category name for the slug field.
const slugify = (value) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const resolveIcon = (icon) => {
  if (!icon) return "";
  if (/^https?:\/\//i.test(icon)) return icon;
  return `${API_ORIGIN}/${String(icon).replace(/^\//, "")}`;
};

const emptyForm = {
  name: "",
  slug: "",
  isAutoParts: false,
  isDutyEstimatorAvailable: false,
  image: null,
  imagePreview: null,
};

function CategoriesManagement() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ open: false, type: "success", title: "", message: "" });

  // ── Add dialog state ──────────────────────────────────────────────
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [addForm, setAddForm] = useState(emptyForm);
  const [addSaving, setAddSaving] = useState(false);
  const addFileRef = useRef(null);

  // ── Edit dialog state ─────────────────────────────────────────────
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm);
  const [editSaving, setEditSaving] = useState(false);
  const editFileRef = useRef(null);

  const showAlert = (type, title, message) => setAlert({ open: true, type, title, message });

  // ── Fetch categories ──────────────────────────────────────────────
  const fetchCategories = async () => {
    setLoading(true);
    try {
      const res = await getCategories();
      const list = res?.data || res?.categories || (Array.isArray(res) ? res : []);
      setCategories(Array.isArray(list) ? list : []);
    } catch (error) {
      showAlert("error", "Error", error?.message || "Failed to load categories.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // ── Add Category ──────────────────────────────────────────────────
  const handleOpenAdd = () => {
    setAddForm(emptyForm);
    setOpenAddDialog(true);
  };

  const handleAddImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAddForm((prev) => ({ ...prev, image: file, imagePreview: URL.createObjectURL(file) }));
    }
  };

  const handleAddCategory = async () => {
    if (!addForm.name.trim()) return;
    setAddSaving(true);
    try {
      const fd = new FormData();
      fd.append("name", addForm.name.trim());
      fd.append("slug", addForm.slug.trim() || slugify(addForm.name));
      fd.append("isAutoParts", addForm.isAutoParts);
      fd.append("isDutyEstimatorAvailable", addForm.isDutyEstimatorAvailable);
      if (addForm.image) fd.append("icon", addForm.image);

      const res = await createCategory(fd);
      if (res?.success === false || res?.error) {
        throw new Error(res?.message || "Failed to create category.");
      }
      showAlert("success", "Added", "Category added successfully.");
      setOpenAddDialog(false);
      fetchCategories();
    } catch (error) {
      showAlert("error", "Error", error?.message || "Failed to create category.");
    } finally {
      setAddSaving(false);
    }
  };

  // ── Edit Category ─────────────────────────────────────────────────
  const handleOpenEdit = (category) => {
    setSelectedId(category.id);
    setEditForm({
      name: category.name || "",
      slug: category.slug || "",
      isAutoParts: !!category.isAutoParts,
      isDutyEstimatorAvailable: !!category.isDutyEstimatorAvailable,
      image: null,
      imagePreview: resolveIcon(category.icon) || null,
    });
    setOpenEditDialog(true);
  };

  const handleEditImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEditForm((prev) => ({ ...prev, image: file, imagePreview: URL.createObjectURL(file) }));
    }
  };

  const handleSaveEdit = async () => {
    if (!editForm.name.trim()) return;
    setEditSaving(true);
    try {
      const fd = new FormData();
      fd.append("name", editForm.name.trim());
      fd.append("slug", editForm.slug.trim() || slugify(editForm.name));
      fd.append("isAutoParts", editForm.isAutoParts);
      fd.append("isDutyEstimatorAvailable", editForm.isDutyEstimatorAvailable);
      if (editForm.image) fd.append("icon", editForm.image);

      const res = await updateCategory(selectedId, fd);
      if (res?.success === false || res?.error) {
        throw new Error(res?.message || "Failed to update category.");
      }
      showAlert("success", "Updated", "Category updated successfully.");
      setOpenEditDialog(false);
      fetchCategories();
    } catch (error) {
      showAlert("error", "Error", error?.message || "Failed to update category.");
    } finally {
      setEditSaving(false);
    }
  };

  const { columns, rows } = categoryTableData({
    categoryList: categories,
    onEditClick: handleOpenEdit,
  });

  // ── Shared image-picker UI ────────────────────────────────────────
  const ImagePicker = ({ preview, fileRef, onChange, label }) => (
    <Box display="flex" flexDirection="column" alignItems="center" gap={1} mt={1} mb={1}>
      <Box position="relative">
        <Avatar
          src={preview}
          alt={label}
          sx={{ width: 90, height: 90, border: "2px dashed #bbb", background: "#f5f5f5" }}
        />
        <IconButton
          size="small"
          onClick={() => fileRef.current?.click()}
          sx={{
            position: "absolute",
            bottom: 0,
            right: 0,
            background: "#1976d2",
            color: "#fff",
            "&:hover": { background: "#115293" },
            width: 28,
            height: 28,
          }}
        >
          <PhotoCameraIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
      <MDTypography variant="caption" color="text">
        {preview ? "Change Icon" : "Upload Icon"}
      </MDTypography>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={onChange}
      />
    </Box>
  );

  // ── Shared form fields ────────────────────────────────────────────
  const CategoryFields = ({ form, setForm }) => (
    <>
      <TextField
        fullWidth
        margin="dense"
        label="Category Name"
        value={form.name}
        onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
      />
      <TextField
        fullWidth
        margin="dense"
        label="Slug"
        placeholder={slugify(form.name) || "auto-generated"}
        value={form.slug}
        onChange={(e) => setForm((prev) => ({ ...prev, slug: e.target.value }))}
      />
      <Box display="flex" flexDirection="column" mt={1}>
        <FormControlLabel
          control={
            <Switch
              checked={form.isAutoParts}
              onChange={(e) => setForm((prev) => ({ ...prev, isAutoParts: e.target.checked }))}
            />
          }
          label={<MDTypography variant="button">Is Auto Parts</MDTypography>}
        />
        <FormControlLabel
          control={
            <Switch
              checked={form.isDutyEstimatorAvailable}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, isDutyEstimatorAvailable: e.target.checked }))
              }
            />
          }
          label={<MDTypography variant="button">Duty Estimator Available</MDTypography>}
        />
      </Box>
    </>
  );
  console.log('categories',categories);
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <AlertBox
        open={alert.open}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        onClose={() => setAlert({ ...alert, open: false })}
      />

      {/* ── Add Category Dialog ── */}
      <Dialog open={openAddDialog} onClose={() => setOpenAddDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add New Category</DialogTitle>
        <DialogContent>
          <ImagePicker
            preview={addForm.imagePreview}
            fileRef={addFileRef}
            onChange={handleAddImageChange}
            label="Category Icon"
          />
          <CategoryFields form={addForm} setForm={setAddForm} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddDialog(false)} disabled={addSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleAddCategory}
            disabled={!addForm.name.trim() || addSaving}
            sx={{ color: "beige" }}
          >
            {addSaving ? <CircularProgress size={18} color="inherit" /> : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Edit Category Dialog ── */}
      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit Category</DialogTitle>
        <DialogContent>
          <ImagePicker
            preview={editForm.imagePreview}
            fileRef={editFileRef}
            onChange={handleEditImageChange}
            label="Category Icon"
          />
          <CategoryFields form={editForm} setForm={setEditForm} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)} disabled={editSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveEdit}
            disabled={!editForm.name.trim() || editSaving}
            sx={{ color: "beige" }}
          >
            {editSaving ? <CircularProgress size={18} color="inherit" /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Main Content ── */}
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  Categories
                </MDTypography>
                <Button
                  variant="contained"
                  onClick={handleOpenAdd}
                  sx={{
                    background: "rgba(255,255,255,0.2)",
                    color: "#fff",
                    fontWeight: "bold",
                    "&:hover": { background: "rgba(255,255,255,0.35)" },
                  }}
                >
                  + Add Category
                </Button>
              </MDBox>

              <MDBox pt={3}>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" alignItems="center" py={5}>
                    <CircularProgress color="info" />
                  </MDBox>
                ) : (
                  <DataTable
                    table={{ columns, rows }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    noEndBorder
                    canSearch={false}
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default CategoriesManagement;
